/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-09-24
 */

import React, { useCallback } from 'react';
import {
  Box,
  TextField,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  getCountryOptions,
  getStateOptions,
  getCountryByName,
  getStateLabel,
  countryHasStates,
} from '../../utils/countryData';

/**
 * International address data interface
 * Compatible with both ProfileEditForm and BillingAddress formats
 */
export interface InternationalAddressData {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

/**
 * Props for the InternationalAddressForm component
 */
export interface InternationalAddressFormProps {
  // Data
  value: InternationalAddressData;
  onChange: (address: InternationalAddressData) => void;

  // Validation
  errors?: Partial<Record<keyof InternationalAddressData, string>>;
  disabled?: boolean;

  // Customization
  required?: boolean;
  compact?: boolean;
  sectionTitle?: string;
  showOptionalLabels?: boolean;
  showGuidance?: boolean;
  requirePostalCode?: boolean; // For billing addresses that need postal code verification

  // Field customization
  fieldNames?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };

  // Styling
  sx?: any;
}

/**
 * InternationalAddressForm component
 *
 * A reusable international address form component that handles:
 * - Country selection with all countries
 * - Dynamic state/province handling based on country
 * - Smart field labels that adapt to selected country
 * - Flexible validation and error handling
 * - Consistent styling with Material-UI
 *
 * @example
 * // Basic usage
 * <InternationalAddressForm
 *   value={addressData}
 *   onChange={setAddressData}
 *   errors={validationErrors}
 * />
 *
 * @example
 * // Billing address usage
 * <InternationalAddressForm
 *   value={billingAddress}
 *   onChange={setBillingAddress}
 *   sectionTitle="Billing Address"
 *   required
 *   compact
 * />
 */
const InternationalAddressForm: React.FC<InternationalAddressFormProps> = ({
  value,
  onChange,
  errors = {},
  disabled = false,
  required = false,
  compact = false,
  sectionTitle,
  showOptionalLabels = true,
  showGuidance = true,
  requirePostalCode = false,
  fieldNames = {},
  sx,
}) => {
  // Default field names
  const labels = {
    line1: fieldNames.line1 || 'Address Line 1',
    line2: fieldNames.line2 || 'Address Line 2',
    city: fieldNames.city || 'City',
    state: fieldNames.state || 'State',
    postalCode: fieldNames.postalCode || 'Postal Code',
    country: fieldNames.country || 'Country',
  };

  /**
   * Handles country selection changes and resets state/province field
   */
  const handleCountryChange = useCallback(
    (event: any) => {
      const countryName = event.target.value;

      onChange({
        ...value,
        country: countryName,
        state: '', // Reset state/province when country changes
      });
    },
    [value, onChange]
  );

  /**
   * Handles state/province selection changes
   */
  const handleStateChange = useCallback(
    (event: any) => {
      const stateName = event.target.value;

      onChange({
        ...value,
        state: stateName,
      });
    },
    [value, onChange]
  );

  /**
   * Handles text field changes
   */
  const handleFieldChange = useCallback(
    (fieldName: keyof InternationalAddressData) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange({
          ...value,
          [fieldName]: event.target.value,
        });
      },
    [value, onChange]
  );

  // Get country code for state handling
  const selectedCountry = getCountryByName(value.country);
  const countryCode = selectedCountry?.code || '';
  const hasStates = countryHasStates(countryCode);
  const stateLabel = getStateLabel(countryCode);

  // Determine if postal code should be required (for billing addresses)
  const isPostalCodeRequired = requirePostalCode || required;

  return (
    <Box sx={sx}>
      {/* Section Title */}
      {sectionTitle && (
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          {sectionTitle}
        </Typography>
      )}

      {/* Address completion guidance */}
      {showGuidance && !compact && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Address Guidelines:</strong> Address information is{' '}
            {required ? 'required' : 'optional'}.
            {!required &&
              ' However, if you choose to provide an address, please complete at least: '}
            <strong>Street Address, City, and Country</strong> for a valid
            address.
          </Typography>
        </Alert>
      )}

      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 2 : 2 }}
      >
        {/* Address Line 1 */}
        <TextField
          fullWidth
          label={labels.line1}
          value={value.line1}
          onChange={handleFieldChange('line1')}
          error={!!errors.line1}
          helperText={errors.line1}
          disabled={disabled}
          required={required}
          placeholder="123 Main Street"
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
              },
              transition: 'all 0.2s ease-in-out',
            },
          }}
        />

        {/* Address Line 2 */}
        <TextField
          fullWidth
          label={`${labels.line2}${showOptionalLabels ? ' (Optional)' : ''}`}
          value={value.line2 || ''}
          onChange={handleFieldChange('line2')}
          error={!!errors.line2}
          helperText={errors.line2}
          disabled={disabled}
          placeholder="Apartment, suite, etc."
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
              },
              transition: 'all 0.2s ease-in-out',
            },
          }}
        />

        {/* Country Selection */}
        <FormControl fullWidth error={!!errors.country} disabled={disabled}>
          <InputLabel id="country-select-label">
            {labels.country}
            {required ? ' *' : ''}
          </InputLabel>
          <Select
            labelId="country-select-label"
            id="country-select"
            value={value.country}
            label={`${labels.country}${required ? ' *' : ''}`}
            onChange={handleCountryChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
                '&.Mui-focused fieldset': {
                  borderWidth: '2px',
                },
                transition: 'all 0.2s ease-in-out',
              },
            }}
          >
            <MenuItem value="">
              <em>Select a country</em>
            </MenuItem>
            {getCountryOptions().map(country => (
              <MenuItem key={country.code} value={country.value}>
                {country.label}
              </MenuItem>
            ))}
          </Select>
          {errors.country && (
            <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
              {errors.country}
            </Typography>
          )}
        </FormControl>

        {/* State/Province/Region Selection */}
        {value.country &&
          (hasStates ? (
            // Show dropdown for countries with states (US, CA, AU, GB)
            <FormControl fullWidth error={!!errors.state} disabled={disabled}>
              <InputLabel id="state-select-label">
                {stateLabel}
                {required ? ' *' : ''}
              </InputLabel>
              <Select
                labelId="state-select-label"
                id="state-select"
                value={value.state}
                label={`${stateLabel}${required ? ' *' : ''}`}
                onChange={handleStateChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '2px',
                    },
                    '&.Mui-focused fieldset': {
                      borderWidth: '2px',
                    },
                    transition: 'all 0.2s ease-in-out',
                  },
                }}
              >
                <MenuItem value="">
                  <em>Select {stateLabel}</em>
                </MenuItem>
                {getStateOptions(countryCode).map(state => (
                  <MenuItem key={state.code} value={state.value}>
                    {state.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.state && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, ml: 2 }}
                >
                  {errors.state}
                </Typography>
              )}
            </FormControl>
          ) : (
            // Show text field for countries without formal states
            <TextField
              fullWidth
              label={`${stateLabel}${showOptionalLabels ? ' (Optional)' : ''}`}
              value={value.state}
              onChange={handleFieldChange('state')}
              error={!!errors.state}
              helperText={errors.state}
              disabled={disabled}
              placeholder="Enter region (optional)"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '2px',
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: '2px',
                  },
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            />
          ))}

        {/* City and Postal Code */}
        <Box
          sx={{
            display: compact ? 'flex' : 'grid',
            gridTemplateColumns: compact ? undefined : '1fr 1fr',
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            label={labels.city}
            value={value.city}
            onChange={handleFieldChange('city')}
            error={!!errors.city}
            helperText={errors.city}
            disabled={disabled}
            required={required}
            placeholder="New York"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
                '&.Mui-focused fieldset': {
                  borderWidth: '2px',
                },
                transition: 'all 0.2s ease-in-out',
              },
            }}
          />
          <TextField
            fullWidth
            label={
              isPostalCodeRequired
                ? labels.postalCode
                : `${labels.postalCode}${showOptionalLabels ? ' (Optional)' : ''}`
            }
            value={value.postalCode}
            onChange={handleFieldChange('postalCode')}
            error={!!errors.postalCode}
            helperText={errors.postalCode}
            disabled={disabled}
            required={isPostalCodeRequired}
            placeholder={(() => {
              switch (countryCode) {
                case 'US':
                  return '10001';
                case 'CA':
                  return 'K1A 0A6';
                case 'GB':
                  return 'SW1A 1AA';
                default:
                  return '12345';
              }
            })()}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
                '&.Mui-focused fieldset': {
                  borderWidth: '2px',
                },
                transition: 'all 0.2s ease-in-out',
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default InternationalAddressForm;
